import React, { useRef, useEffect, useCallback } from "react";
import { Grid } from "@sumprio/ui";
import { NoteIcon } from "@sumprio/ui/icons";
import { AppChatInput } from "../AppChatInput";
import EmptyState from "../EmptyState";
import { NoteFeedItem } from "./NoteFeedItem";
import type { FeedItem, NotesFeedPanelProps } from "./types";

const DEFAULT_EMPTY_TITLE = "No notes added yet";
const DEFAULT_EMPTY_DESCRIPTION =
  "Add notes to track updates, share insights, or collaborate with your team.";
const DEFAULT_MIN_HEIGHT = 240;

export function NotesFeedPanel({
  items,
  emptyTitle = DEFAULT_EMPTY_TITLE,
  emptyDescription = DEFAULT_EMPTY_DESCRIPTION,
  emptyIcon,
  showInput = true,
  inputValue = "",
  onInputChange,
  onPost,
  posting = false,
  inputPlaceholder = "Leave your note here...",
  sendButtonLabel = "Post",
  minHeight = DEFAULT_MIN_HEIGHT,
  showAttachmentIcon = false,
  inputAutoFocus = false,
  onEditNote,
  onDeleteNote,
  editingNoteId = null,
}: NotesFeedPanelProps) {
  const hasItems = Array.isArray(items) && items.length > 0;
  const icon = emptyIcon ?? <NoteIcon size="xl" />;
  const listScrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const prevPostingRef = useRef(posting);
  const firstItemKey = items?.[0]?.id ?? items?.[0]?.timestamp ?? "";

  const focusComposerTextarea = useCallback(() => {
    const root = composerRef.current;
    const ta = root?.querySelector("textarea");
    ta?.focus();
  }, []);

  useEffect(() => {
    if (hasItems && listScrollRef.current) {
      listScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [hasItems, items.length, firstItemKey]);

  useEffect(() => {
    if (!inputAutoFocus || !showInput) return;
    const id = window.requestAnimationFrame(() => {
      focusComposerTextarea();
    });
    return () => window.cancelAnimationFrame(id);
  }, [inputAutoFocus, showInput, focusComposerTextarea]);

  useEffect(() => {
    const wasPosting = prevPostingRef.current;
    prevPostingRef.current = posting;
    if (!inputAutoFocus || !showInput) return;
    if (wasPosting && !posting) {
      const id = window.requestAnimationFrame(() => {
        focusComposerTextarea();
      });
      return () => window.cancelAnimationFrame(id);
    }
  }, [posting, inputAutoFocus, showInput, focusComposerTextarea]);

  return (
    <Grid
      className="notes-feed-panel sticky-messages-panel"
      display="flex"
      flexDirection="column"
      gap="md"
      flex={1}
      width="100%"
      style={{ minHeight: 0, height: "100%", maxHeight: "100%" }}
    >
      <div ref={listScrollRef} className="sticky-messages-panel__list" style={{ paddingTop: 4 }}>
        {!hasItems ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              minHeight: `min(100%, max(${minHeight}px, 35vh))`,
              padding: "16px 8px",
            }}
          >
            <EmptyState icon={icon} title={emptyTitle} description={emptyDescription} />
          </div>
        ) : (
          <Grid display="flex" flexDirection="column" gap="md" width="100%">
            {items.map((item: FeedItem) => (
              <NoteFeedItem
                key={item.id ?? item.timestamp ?? Math.random()}
                name={item.name}
                initials={item.initials}
                message={item.message}
                timestamp={item.timestamp}
                avatarSize="sm"
                isEditing={
                  editingNoteId !== null &&
                  editingNoteId !== undefined &&
                  item.id !== undefined &&
                  String(item.id) === String(editingNoteId)
                }
                onEdit={onEditNote ? () => onEditNote(item) : undefined}
                onDelete={onDeleteNote ? () => onDeleteNote(item) : undefined}
              />
            ))}
          </Grid>
        )}
      </div>
      {showInput && (
        <div ref={composerRef} className="sticky-messages-panel__composer">
          <AppChatInput
            value={inputValue}
            onChange={onInputChange ?? (() => {})}
            onSend={onPost ?? (() => {})}
            placeholder={inputPlaceholder}
            sendButtonLabel={sendButtonLabel}
            disabled={posting}
            width="full"
            minRows={1}
            maxRows={4}
            showAttachmentIcon={showAttachmentIcon}
            autoFocus={inputAutoFocus}
          />
        </div>
      )}
    </Grid>
  );
}
